/**
 * Admin Brand Retention page — EX-C Step 6.
 *
 * Super-admin per-brand mirror of the Owner Retention page. Consumes the
 * same shared <RetentionPanel /> component as the Owner surface; the
 * only behavioural differences live at this layer:
 *   - brand_id is taken from the URL path param (admin is not scoped to
 *     a single brand the way an owner is)
 *   - `showEmail={false}` on the panel double-protects the PII gate
 *     that the admin endpoint already enforces server-side
 *   - row-click navigates into the admin CRM customer detail
 *
 * Cross-brand isolation is preserved by `assertBrandMatch()` on the
 * response and by including `brand_id` in the React Query cache key
 * tuple (qk.adminBrandInactive).
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { qk, matchByBrand } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { assertBrandMatch } from '@/lib/utils';
import {
  PageHeader, FullSpinner, QueryError,
} from '@/components/ui_primitives/Misc';
import Button from '@/components/ui_primitives/Button';
import RetentionPanel from '@/components/RetentionPanel';

const PAGE_LIMIT = 50;

export default function AdminBrandRetentionPage() {
  const { brand_id: brandId } = useParams();
  const token = useAuthStore((s) => s.token);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [thresholdDays, setThresholdDays] = useState(30);
  const [offset, setOffset] = useState(0);

  // Brand switch invalidation — same pattern as
  // AdminBrandDrilldownPage. Prevents the previous brand's retention
  // data from briefly flashing on the new brand's page.
  useEffect(() => {
    queryClient.invalidateQueries({ predicate: matchByBrand(brandId) });
  }, [brandId, queryClient]);

  const inactiveQ = useQuery({
    queryKey: qk.adminBrandInactive(brandId, thresholdDays, PAGE_LIMIT, offset),
    queryFn: async () =>
      assertBrandMatch(
        await api.adminBrandInactiveCustomers(
          token, brandId, thresholdDays, PAGE_LIMIT, offset,
        ),
        brandId,
      ),
    enabled: !!token && !!brandId,
    keepPreviousData: true,
  });

  const handleThresholdChange = (newDays, newOffset) => {
    if (typeof newOffset === 'number') {
      setOffset(newOffset);
      return;
    }
    setThresholdDays(newDays);
    setOffset(0);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: qk.adminBrandInactive(brandId, thresholdDays, PAGE_LIMIT, offset),
    });
  };

  const handleCustomerClick = (item) => {
    if (!item?.customer_id) return;
    navigate(`/admin/brands/${brandId}/crm/${item.customer_id}`);
  };

  if (inactiveQ.isLoading) return <FullSpinner />;
  if (inactiveQ.error)
    return <QueryError error={inactiveQ.error} onRetry={() => inactiveQ.refetch()} />;

  const data = inactiveQ.data || {};

  return (
    <div className="space-y-6" data-testid="admin-brand-retention-page">
      <div className="flex items-center gap-2">
        <Link to={`/admin/brands/${brandId}`}>
          <Button variant="ghost" size="sm" leftIcon={<ArrowLeft size={12} />}>
            Back to brand
          </Button>
        </Link>
      </div>

      <PageHeader
        kicker={data.brand_name ? `Brand · ${data.brand_name}` : 'Retention'}
        title="Customer Retention"
        subtitle={
          data.brand_name
            ? `Inactive customer detection for ${data.brand_name}.`
            : 'Inactive customer detection.'
        }
      />

      <RetentionPanel
        data={data}
        thresholdDays={thresholdDays}
        onThresholdChange={handleThresholdChange}
        onRefresh={handleRefresh}
        onCustomerClick={handleCustomerClick}
        isLoading={inactiveQ.isLoading}
        isFetching={inactiveQ.isFetching}
        showEmail={false}
      />
    </div>
  );
}
